import type { IJobApplicationStore } from 'src/stores/JobApplicationStore';
import { useStores } from 'src/utils/hooks/useStores';
import {
  type FiltersInteractedAttributes,
  trackFiltersInteracted,
  trackMustHaveFilterInteracted,
  trackParentFilterInteractedOff,
  trackParentFilterInteractedOn,
} from 'src/utils/events';
import { sendHotjarTag } from 'src/utils/hotjar';
import { HOTJAR_FILTERS_INTERACTED } from 'src/constants/hotjar';
import {
  TOGGLE_STATE_ON,
  TOGGLE_STATE_OFF,
  MUST_HAVE_TOGGLE_TYPE,
} from 'src/constants/toggle';
import type {
  EVENT_NAME_CLEAR_FILTERS_BUTTON_PRESSED,
  EVENT_NAME_FILTERS_INTERACTED,
} from 'src/constants/pageTrackingData';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

export interface TrackingData {
  eventName:
    | typeof EVENT_NAME_FILTERS_INTERACTED
    | typeof EVENT_NAME_CLEAR_FILTERS_BUTTON_PRESSED;
  actionOrigin:
    | 'candidate listing - filter section'
    | 'candidate listing - centre cta with must have'
    | 'candidate listing - centre cta without must have';
}

export const shouldShowFilterSection = ({
  searchApiEnabled,
  hasFilterQuestions,
  showFilterError,
}: {
  searchApiEnabled: boolean;
  hasFilterQuestions: boolean;
  showFilterError: boolean;
}) => {
  if (!searchApiEnabled) {
    return false;
  }
  if (hasFilterQuestions || showFilterError) {
    return true;
  }
  return false;
};

export const useFilters = () => {
  const {
    jobApplicationStore,
    jobStore: {
      questionnaire,
      job: {
        jobInformation: { jobId },
      },
    },
    seekAdvertiserId,
    userStore: {
      user: { userId },
    },
    featureStore,
  } = useStores();
  const { language } = useAppConfig();

  const { filtersStore, loadJobApplications } =
    jobApplicationStore as IJobApplicationStore;

  const resetFilters = (trackingData: TrackingData) => {
    if (filtersStore.hasSelectedFilter) {
      filtersStore.clearFilters();
      jobApplicationStore.clearAllSelected();
      trackFiltersEvent(trackingData);
      loadJobApplications({ language });
    }
  };

  const trackingAttributes = {
    seekAdvertiserId,
    seekListingId: jobId,
  };

  const trackFiltersEvent = ({ eventName, actionOrigin }: TrackingData) => {
    if (!userId) {
      return;
    }

    const attributes: FiltersInteractedAttributes = {
      ...trackingAttributes,
      isCandidateListFiltered: filtersStore.hasSelectedFilter,
      userId,
    };
    if (actionOrigin) {
      attributes.actionOrigin = actionOrigin;
    }

    trackFiltersInteracted(eventName, attributes);
    sendHotjarTag(HOTJAR_FILTERS_INTERACTED);
  };

  const trackParentFilterEvent = (isSelected: boolean) => {
    if (!userId) {
      return;
    }
    sendHotjarTag(HOTJAR_FILTERS_INTERACTED);
    if (isSelected) {
      trackParentFilterInteractedOn({
        ...trackingAttributes,
        isCandidateListFiltered: filtersStore.hasSelectedFilter,
        userId,
      });
      return;
    }
    trackParentFilterInteractedOff({
      ...trackingAttributes,
      isCandidateListFiltered: filtersStore.hasSelectedFilter,
      userId,
    });
  };

  const trackMustHaveFilterEvent = () => {
    if (!userId) {
      return;
    }
    sendHotjarTag(HOTJAR_FILTERS_INTERACTED);
    trackMustHaveFilterInteracted({
      ...trackingAttributes,
      isCandidateListFiltered: filtersStore.hasSelectedFilter,
      userId,
      toggleState: filtersStore.isMustHaveToggleSelected
        ? TOGGLE_STATE_ON
        : TOGGLE_STATE_OFF,
      toggleType: MUST_HAVE_TOGGLE_TYPE,
    });
  };

  return {
    trackFiltersEvent,
    trackParentFilterEvent,
    trackMustHaveFilterEvent,
    showFiltersSection: shouldShowFilterSection({
      searchApiEnabled: featureStore?.isSearchApiFeatureEnabled,
      hasFilterQuestions: filtersStore?.hasFilterQuestions,
      showFilterError: questionnaire?.isError,
    }),
    resetFilters,
  };
};
